import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Download, Upload, Loader2, Car } from "lucide-react";
import api, { formatApiErrorDetail, downloadAuthedFile } from "@/lib/api";
import { loadModelViewer } from "@/components/creator/GlbPreview";

const inputCls = "bg-[#050505] border border-white/10 px-2.5 py-2 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue";
const labelCls = "font-mono text-[8px] tracking-[0.2em] text-zinc-500 uppercase block mb-1";

const DEFAULT_WHEELS = [
  { bone: "wheel_fl", radius: 34, width: 24, travel: 12, stiffness: 38, damping: 4.2, drive: false, steer: true },
  { bone: "wheel_fr", radius: 34, width: 24, travel: 12, stiffness: 38, damping: 4.2, drive: false, steer: true },
  { bone: "wheel_rl", radius: 34, width: 24, travel: 12, stiffness: 38, damping: 4.2, drive: true, steer: false },
  { bone: "wheel_rr", radius: 34, width: 24, travel: 12, stiffness: 38, damping: 4.2, drive: true, steer: false },
];

function Num({ label, value, onChange, step = 1, min, max }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input type="number" step={step} min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className={`${inputCls} w-full`} />
    </div>
  );
}

export default function FGearLab() {
  const [me, setMe] = useState(null);
  const [chassis, setChassis] = useState({ mass: 1400, com_x: 0, com_y: 0, com_z: -20, drag: 0.35, downforce: 0.1 });
  const [engine, setEngine] = useState({ max_torque: 380, max_rpm: 6800, final_drive: 3.42, gears: [3.2, 2.1, 1.6, 1.2, 1.0, 0.85] });
  const [wheels, setWheels] = useState(DEFAULT_WHEELS);
  const [name, setName] = useState("Interceptor Rig");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [itemId, setItemId] = useState("");
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const [modelUrl, setModelUrl] = useState("");
  const [modelSize, setModelSize] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    loadModelViewer().catch(() => {});
    api.get("/auth/me").then((r) => { setMe(r.data); loadMine(); }).catch(() => setMe(null));
    return () => modelUrl && URL.revokeObjectURL(modelUrl);
  }, []);

  const loadMine = () => api.get("/ncmod/fgear/mine").then((r) => setMine(r.data.items || [])).catch(() => {});

  const config = () => ({ chassis, engine, wheels });

  const save = async () => {
    if (!me) { toast.error("Sign in to save rigs"); return null; }
    setBusy(true);
    try {
      const payload = { name, description, config: config(), public: isPublic };
      const r = itemId ? await api.patch(`/ncmod/fgear/${itemId}`, payload) : await api.post("/ncmod/fgear", payload);
      setItemId(r.data.id);
      toast.success(itemId ? "Rig updated" : "Rig saved — now attach your vehicle model");
      loadMine();
      return r.data.id;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onModelPick = async (file) => {
    if (!file) return;
    if (modelUrl) URL.revokeObjectURL(modelUrl);
    setModelUrl(URL.createObjectURL(file));
    setModelSize(file.size);
    if (!me) { toast.error("Sign in to attach models"); return; }
    const id = itemId || (await save());
    if (!id) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const tok = localStorage.getItem("nc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ncmod/fgear/${id}/model`, {
        method: "POST", headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/octet-stream" }, body: file,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Upload failed");
      toast.success("Model attached — preview below");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadItem = (item) => {
    setItemId(item.id);
    setName(item.name);
    setDescription(item.description || "");
    setChassis(item.config.chassis);
    setEngine(item.config.engine);
    setWheels(item.config.wheels);
    setIsPublic(item.public || false);
    toast.success(`Loaded "${item.name}"`);
  };

  const exportINI = () => {
    if (!itemId || !isPublic) { toast.error("Save + share publicly first to export the FGear INI"); return; }
    downloadAuthedFile(`/ncmod/fgear/${itemId}/export.ini`, `${name.replace(/\s+/g, "_")}.ini`).catch((e) => toast.error(e.message));
  };

  const setWheel = (i, k, v) => setWheels((ws) => ws.map((w, j) => (j === i ? { ...w, [k]: v } : w)));

  return (
    <div data-testid="fgear-lab" className="flex flex-col gap-6">
      {/* model import + preview */}
      <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3">
        <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><Car className="w-3.5 h-3.5" /> Vehicle model — import your GLB</p>
        <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
          Export your vehicle from Blender as <b className="text-zinc-300">binary GLB</b> (Y-up, meters, wheels as separate bones named wheel_fl/fr/rl/rr).
          Attach it here to preview in 3D while you rig. Max 50MB.
        </p>
        <div className="flex gap-2 items-center flex-wrap">
          <button data-testid="model-upload" onClick={() => fileRef.current?.click()} className="font-mono text-[10px] tracking-[0.15em] uppercase bg-nc-blue hover:bg-nc-blue/80 text-white px-4 py-2.5 transition-colors flex items-center gap-2">
            <Upload className="w-3.5 h-3.5" /> Upload GLB
          </button>
          <input ref={fileRef} type="file" accept=".glb" className="hidden" onChange={(e) => onModelPick(e.target.files?.[0])} />
          {modelSize > 0 && <span className="font-mono text-[9px] text-zinc-500">{(modelSize / 1048576).toFixed(1)} MB</span>}
        </div>
        {modelUrl && (
          <div data-testid="model-preview" className="border border-white/10 bg-[#030303]">
            <model-viewer src={modelUrl} camera-controls auto-rotate auto-rotate-delay="0" shadow-intensity="1" style={{ width: "100%", height: "360px" }} />
          </div>
        )}
      </div>

      {/* chassis + engine */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-black/40 p-5">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase mb-4">Chassis</p>
          <div className="grid grid-cols-2 gap-3">
            <Num label="Mass (kg)" value={chassis.mass} min={100} max={10000} onChange={(v) => setChassis((c) => ({ ...c, mass: v }))} />
            <Num label="Drag coefficient" value={chassis.drag} step={0.01} min={0} max={1.5} onChange={(v) => setChassis((c) => ({ ...c, drag: v }))} />
            <Num label="CoM offset X" value={chassis.com_x} onChange={(v) => setChassis((c) => ({ ...c, com_x: v }))} />
            <Num label="CoM offset Z" value={chassis.com_z} onChange={(v) => setChassis((c) => ({ ...c, com_z: v }))} />
            <Num label="Downforce" value={chassis.downforce} step={0.05} min={0} max={5} onChange={(v) => setChassis((c) => ({ ...c, downforce: v }))} />
          </div>
        </div>
        <div className="border border-white/10 bg-black/40 p-5">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase mb-4">Engine & drivetrain</p>
          <div className="grid grid-cols-2 gap-3">
            <Num label="Max torque (Nm)" value={engine.max_torque} min={50} max={3000} onChange={(v) => setEngine((e) => ({ ...e, max_torque: v }))} />
            <Num label="Max RPM" value={engine.max_rpm} min={2000} max={14000} step={100} onChange={(v) => setEngine((e) => ({ ...e, max_rpm: v }))} />
            <Num label="Final drive" value={engine.final_drive} step={0.01} min={1} max={6} onChange={(v) => setEngine((e) => ({ ...e, final_drive: v }))} />
            <div>
              <label className={labelCls}>Gear ratios (comma separated)</label>
              <input value={engine.gears.join(", ")} onChange={(e) => setEngine((en) => ({ ...en, gears: e.target.value.split(",").map((x) => parseFloat(x)).filter((n) => !isNaN(n)).slice(0, 10) }))} className={`${inputCls} w-full`} />
            </div>
          </div>
        </div>
      </div>

      {/* wheels */}
      <div className="border border-white/10 bg-black/40 p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase">Wheels & suspension ({wheels.length})</p>
          <button onClick={() => wheels.length < 8 && setWheels((w) => [...w, { bone: `wheel_${w.length}`, radius: 34, width: 24, travel: 12, stiffness: 38, damping: 4.2, drive: false, steer: false }])} className="font-mono text-[9px] uppercase border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 px-2.5 py-1.5 transition-colors flex items-center gap-1"><Plus className="w-3 h-3" /> Wheel</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {wheels.map((w, i) => (
            <div key={i} data-testid={`wheel-${i}`} className="border border-white/10 bg-[#050505] p-4 flex flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <input value={w.bone} onChange={(e) => setWheel(i, "bone", e.target.value.slice(0, 24))} className="bg-black/40 border border-white/10 px-2 py-1 font-mono text-[10px] text-nc-blue rounded-none focus:outline-none w-32" />
                <div className="flex items-center gap-3">
                  <label className="font-mono text-[8px] text-zinc-500 uppercase flex items-center gap-1"><input type="checkbox" checked={w.drive} onChange={(e) => setWheel(i, "drive", e.target.checked)} className="accent-nc-orange" /> Drive</label>
                  <label className="font-mono text-[8px] text-zinc-500 uppercase flex items-center gap-1"><input type="checkbox" checked={w.steer} onChange={(e) => setWheel(i, "steer", e.target.checked)} className="accent-nc-orange" /> Steer</label>
                  {wheels.length > 2 && <button onClick={() => setWheels((ws) => ws.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Num label="Radius cm" value={w.radius} min={10} max={100} onChange={(v) => setWheel(i, "radius", v)} />
                <Num label="Width cm" value={w.width} min={5} max={60} onChange={(v) => setWheel(i, "width", v)} />
                <Num label="Travel cm" value={w.travel} min={1} max={40} onChange={(v) => setWheel(i, "travel", v)} />
                <Num label="Stiffness" value={w.stiffness} min={5} max={200} onChange={(v) => setWheel(i, "stiffness", v)} />
                <Num label="Damping" value={w.damping} step={0.1} min={0.5} max={20} onChange={(v) => setWheel(i, "damping", v)} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* save / share / export */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase">Save & share</p>
          <input data-testid="rig-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className="bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue" />
          <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} className="bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue" placeholder="Short description (optional)" />
          <label className="flex items-center gap-2 font-mono text-[10px] text-zinc-400 cursor-pointer">
            <input data-testid="rig-public" type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="accent-nc-orange" />
            Share publicly in the Mod Hub library
          </label>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="rig-save" onClick={save} disabled={busy || name.trim().length < 3} className="font-mono text-[10px] tracking-[0.15em] uppercase bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-4 py-2.5 transition-colors flex items-center gap-2">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {itemId ? "Update" : "Save"}
            </button>
            <button data-testid="export-ini" onClick={exportINI} className="font-mono text-[10px] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-4 py-2.5 transition-colors flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> FGear INI
            </button>
          </div>
          <p className="font-mono text-[8px] text-zinc-600 leading-relaxed">The INI maps 1:1 onto FGear's StandardVehicle parameters — paste the values into your blueprint and the vehicle drives with your exact tune. Share publicly to unlock export.</p>
        </div>
        <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-2">
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-1">My saved rigs</p>
          {!me && <p className="font-mono text-[10px] text-zinc-600">Sign in to save and load your rigs.</p>}
          {me && mine.length === 0 && <p className="font-mono text-[10px] text-zinc-600">Nothing saved yet.</p>}
          {mine.map((item) => (
            <div key={item.id} data-testid={`mine-rig-${item.id.slice(0, 8)}`} className="flex items-center justify-between border border-white/5 bg-[#050505] px-3 py-2">
              <button onClick={() => loadItem(item)} className="font-mono text-[10px] text-zinc-200 hover:text-nc-blue transition-colors text-left">{item.name}</button>
              <button onClick={async () => { await api.delete(`/ncmod/fgear/${item.id}`); loadMine(); if (itemId === item.id) setItemId(""); }} className="text-zinc-600 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
